// GoldenRatioAnalyzer.tsx — checks a formula's protein blend against a target
// split (the "golden ratio": 25 hydrolysed / 55 isolate / 20 pulse flour).
//
// Read-only. Every number is computed in the browser from rows already loaded
// through the normal Supabase read path. Nothing is written, nothing persists.
//
// The unresolved question this screen exists to answer: is the target a ratio
// of INGREDIENT WEIGHT or of PROTEIN CONTRIBUTED? Both bases are computed and
// shown side by side, always. The user is never asked to pick one.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Scales, Warning, ArrowUp, ArrowDown, CheckCircle, Question,
} from "@phosphor-icons/react";

import { supabase } from "./supabase/auth";

import { Bucket, COUNTED, classify } from "./engine/goldenRatio";
import { loadGrTargets, saveGrTargets } from "./engine/goldenRatioTargets";

const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const CARD_BG = "rgba(15, 11, 42, 0.85)";
const BORDER = "rgba(139,92,246,0.2)";
const ACCENT = "#f0abfc";

/* ── Types ───────────────────────────────────────────────────────────────── */

interface FormulaRow {
  formula_code: string;
  display_name: string | null;
  dough_ratio_pct: number | null;
}

interface IngredientRow {
  id: string;
  common_name: string;
  protein_per_100g: number | null;
  tags: string[] | string | null;
  /** Authoritative classification. NULL = not yet classified. Read only. */
  protein_class: string | null;
}

interface ComponentRow {
  formula_code: string;
  component: string | null;
  ingredient_id: string | null;
  grams: number | null;
  pct_of_component: number | null;
  row_type: string | null;
}

type Scope = "DOUGH" | "FILLING" | "BOTH";

const ALL_BUCKETS: Bucket[] = ["hydro", "isolate", "pulse", "concentrate", "none"];

/** One line in the classification table — an ingredient, not a row. Duplicate
 *  rows for the same ingredient are summed into one entry, never keyed by name. */
interface Entry {
  ingredientId: string;
  name: string;
  proteinPer100g: number;
  grams: number;
  autoBucket: Bucket;
  /** false = derived from the name heuristic because protein_class is NULL. */
  trusted: boolean;
}

/* ── Grams resolution ────────────────────────────────────────────────────── */

/** Nominal weight of a component on a 100 g finished basis. Only used when a
 *  component carries no usable grams at all. */
function componentBasis(component: string, doughRatioPct: number): number {
  if (component === "DOUGH") return doughRatioPct;
  if (component === "FILLING") return 100 - doughRatioPct;
  return 100;
}

/** Grams per row. Falls back to pct_of_component against the component total
 *  when grams is null or 0. Returns a parallel array, index-aligned to `rows`. */
function resolveGrams(rows: ComponentRow[], doughRatioPct: number): number[] {
  const byComponent: Record<string, number[]> = {};
  rows.forEach((r, i) => {
    const key = r.component || "OTHER";
    (byComponent[key] = byComponent[key] || []).push(i);
  });

  const out = new Array<number>(rows.length).fill(0);

  Object.keys(byComponent).forEach((key) => {
    const idxs = byComponent[key];
    let knownGrams = 0;
    let knownPct = 0;
    let hasPctOnlyRow = false;
    idxs.forEach((i) => {
      const g = rows[i].grams;
      if (g && g > 0) {
        knownGrams += g;
        knownPct += rows[i].pct_of_component || 0;
      } else if ((rows[i].pct_of_component || 0) > 0) {
        hasPctOnlyRow = true;
      }
    });

    let total: number;
    if (knownPct > 0) total = knownGrams / knownPct;
    // When pct-only rows share a component with grams-only rows (their pct is
    // null), knownGrams covers just the weighed rows, so using it as the total
    // would underestimate the pct-only rows. That mix is underdetermined —
    // fall back to the nominal component weight.
    else if (knownGrams > 0 && !hasPctOnlyRow) total = knownGrams;
    else total = componentBasis(key, doughRatioPct);

    idxs.forEach((i) => {
      const g = rows[i].grams;
      out[i] = g && g > 0 ? g : (rows[i].pct_of_component || 0) * total;
    });
  });

  return out;
}

/* ── Formatting ──────────────────────────────────────────────────────────── */

function fmt(n: number, dp: number): string {
  if (!isFinite(n)) return "—";
  return n.toFixed(dp);
}

function signed(n: number, dp: number): string {
  if (!isFinite(n)) return "—";
  const s = n.toFixed(dp);
  return n > 0 ? `+${s}` : s;
}

/* ── Screen ──────────────────────────────────────────────────────────────── */

export default function GoldenRatioAnalyzer() {
  const { t, i18n } = useTranslation();
  const lang: "en" | "ko" = i18n.language === "ko" ? "ko" : "en";

  const L = useCallback(
    (key: string, en: string, ko: string): string =>
      t(key, { defaultValue: lang === "ko" ? ko : en }) as string,
    [t, lang]
  );

  const BUCKET_LABEL: Record<Bucket, string> = {
    hydro: L("goldenRatio.bucket.hydro", "Hydrolysed", "가수분해"),
    isolate: L("goldenRatio.bucket.isolate", "Isolate", "분리단백"),
    pulse: L("goldenRatio.bucket.pulse", "Pulse flour", "두류 분말"),
    concentrate: L("goldenRatio.bucket.concentrate", "Concentrate", "농축단백"),
    none: L("goldenRatio.bucket.none", "Not counted", "제외"),
  };

  const [formulas, setFormulas] = useState<FormulaRow[]>([]);
  const [ingredients, setIngredients] = useState<Record<string, IngredientRow>>({});
  const [picked, setPicked] = useState("");
  const [components, setComponents] = useState<ComponentRow[]>([]);
  const [scope, setScope] = useState<Scope>("BOTH");
  const [loading, setLoading] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);

  // Targets — editable, kept as strings so the inputs can be emptied. Shared
  // with the formula-sheet card through localStorage, so editing either view
  // updates both (engine/goldenRatioTargets.ts).
  const [target, setTarget] = useState<Record<"hydro" | "isolate" | "pulse", string>>(
    loadGrTargets
  );
  useEffect(() => {
    saveGrTargets(target);
  }, [target]);

  // In-memory only. Never written back to Supabase.
  const [overrides, setOverrides] = useState<Record<string, Bucket>>({});

  /* ── Load formulas + ingredients ───────────────────────────────────────── */

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [fRes, iRes] = await Promise.all([
          supabase
            .from("formulas")
            .select("formula_code, display_name, dough_ratio_pct")
            .eq("is_active", true)
            .order("formula_code"),
          supabase
            .from("ingredients")
            .select("id, common_name, protein_per_100g, tags, protein_class")
            .eq("is_active", true),
        ]);
        if (cancelled) return;
        if (fRes.error) throw fRes.error;
        if (iRes.error) throw iRes.error;

        setFormulas((fRes.data as FormulaRow[]) || []);

        const map: Record<string, IngredientRow> = {};
        for (const row of (iRes.data as IngredientRow[]) || []) {
          map[row.id] = row;
        }
        setIngredients(map);
      } catch (err) {
        console.error("GoldenRatioAnalyzer: failed to load reference data", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Load the picked formula's rows ────────────────────────────────────── */

  useEffect(() => {
    if (!picked) {
      setComponents([]);
      return;
    }
    let cancelled = false;
    setLoadingRows(true);
    async function load() {
      try {
        const { data, error } = await supabase
          .from("formula_components")
          .select("formula_code, component, ingredient_id, grams, pct_of_component, row_type")
          .eq("formula_code", picked);
        if (cancelled) return;
        if (error) throw error;
        setComponents((data as ComponentRow[]) || []);
      } catch (err) {
        console.error(`GoldenRatioAnalyzer: failed to load ${picked}`, err);
        if (!cancelled) setComponents([]);
      } finally {
        if (!cancelled) setLoadingRows(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [picked]);

  // A new formula clears the manual overrides — they belong to the old one.
  useEffect(() => {
    setOverrides({});
  }, [picked]);

  /* ── Build entries for the current scope ───────────────────────────────── */

  const doughRatioPct = useMemo(() => {
    const f = formulas.find((x) => x.formula_code === picked);
    return f && f.dough_ratio_pct != null ? f.dough_ratio_pct : 100;
  }, [formulas, picked]);

  const entries: Entry[] = useMemo(() => {
    const rows = components.filter(
      (r) => (r.row_type || "ingredient") === "ingredient" && r.ingredient_id
    );
    const grams = resolveGrams(rows, doughRatioPct);

    // Sum duplicate rows per ingredient id. Keyed by id, never by name.
    const acc = new Map<string, Entry>();
    rows.forEach((r, i) => {
      const comp = r.component || "";
      if (scope !== "BOTH" && comp !== scope) return;

      const id = r.ingredient_id as string;
      const ing = ingredients[id];
      const name = ing ? ing.common_name : id;
      const protein = ing && ing.protein_per_100g != null ? ing.protein_per_100g : 0;

      const existing = acc.get(id);
      if (existing) {
        existing.grams += grams[i];
      } else {
        const c = classify(
          name,
          id,
          protein,
          ing ? ing.tags : null,
          ing ? ing.protein_class : null
        );
        acc.set(id, {
          ingredientId: id,
          name,
          proteinPer100g: protein,
          grams: grams[i],
          autoBucket: c.bucket,
          trusted: c.trusted,
        });
      }
    });

    return Array.from(acc.values()).sort((a, b) => b.grams - a.grams);
  }, [components, ingredients, scope, doughRatioPct]);

  const bucketOf = useCallback(
    (e: Entry): Bucket => overrides[e.ingredientId] || e.autoBucket,
    [overrides]
  );

  /* ── The two bases ─────────────────────────────────────────────────────── */

  /** A guessed row counts toward "provisional" when it either lands in a
   *  counted bucket now, or carries enough protein that a reclassification
   *  would move the result. */
  const isGuess = useCallback(
    (e: Entry): boolean => !e.trusted && !overrides[e.ingredientId],
    [overrides]
  );

  const totals = useMemo(() => {
    const zero: Record<Bucket, number> = {
      hydro: 0, isolate: 0, pulse: 0, concentrate: 0, none: 0,
    };
    const weight: Record<Bucket, number> = { ...zero };
    const protein: Record<Bucket, number> = { ...zero };
    let guessCount = 0;
    for (const e of entries) {
      const b = bucketOf(e);
      if (isGuess(e) && (COUNTED.indexOf(b) >= 0 || e.proteinPer100g >= 50)) {
        guessCount += 1;
      }
      weight[b] += e.grams;
      protein[b] += (e.grams * e.proteinPer100g) / 100;
    }
    // Only the three target buckets are summed. Concentrate and Not counted
    // sit outside the ratio by design.
    const weightSum = COUNTED.reduce((s, b) => s + weight[b], 0);
    const proteinSum = COUNTED.reduce((s, b) => s + protein[b], 0);
    return { weight, protein, weightSum, proteinSum, guessCount };
  }, [entries, bucketOf, isGuess]);

  const targetNum = useMemo(
    () => ({
      hydro: parseFloat(target.hydro) || 0,
      isolate: parseFloat(target.isolate) || 0,
      pulse: parseFloat(target.pulse) || 0,
    }),
    [target]
  );

  const targetSum = targetNum.hydro + targetNum.isolate + targetNum.pulse;
  const targetOff = Math.abs(targetSum - 100) > 0.05;

  const rows = COUNTED.map((b) => {
    const tgt = targetNum[b as "hydro" | "isolate" | "pulse"];
    const byWeight = totals.weightSum > 0 ? (totals.weight[b] / totals.weightSum) * 100 : NaN;
    const byProtein = totals.proteinSum > 0 ? (totals.protein[b] / totals.proteinSum) * 100 : NaN;
    const targetGrams = (tgt / 100) * totals.weightSum;
    return {
      bucket: b,
      target: tgt,
      byWeight,
      weightDelta: byWeight - tgt,
      byProtein,
      proteinDelta: byProtein - tgt,
      grams: totals.weight[b],
      gramsDelta: targetGrams - totals.weight[b],
    };
  });

  const hasData = picked && entries.length > 0;

  /* ── Render ────────────────────────────────────────────────────────────── */

  return (
    <div
      style={{
        minHeight: "calc(100vh - 96px)",
        color: "#f8fafc",
        fontFamily: FONT_STACK,
        padding: "24px 24px 80px",
        position: "relative",
        zIndex: 1,
      }}
    >
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
          <Scales size={40} weight="duotone" color={ACCENT} />
          <div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: "-0.5px" }}>
              {L("goldenRatio.title", "Golden Ratio", "골든 비율")}
            </h1>
            <p style={{ margin: "2px 0 0", color: "#94a3b8", fontSize: 13 }}>
              {L("goldenRatio.desc", "Protein blend vs target", "단백질 배합 대 목표")}
            </p>
          </div>
        </div>

        {/* Controls */}
        <Card>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-end" }}>
            {/* Formula */}
            <Field label={L("goldenRatio.formula", "Formula", "배합")}>
              <select
                value={picked}
                onChange={(e) => setPicked(e.target.value)}
                disabled={loading}
                style={selectStyle(220)}
              >
                <option value="">
                  {loading
                    ? L("goldenRatio.loading", "Loading", "불러오는 중")
                    : L("goldenRatio.pick", "Pick one", "선택")}
                </option>
                {formulas.map((f) => (
                  <option key={f.formula_code} value={f.formula_code}>
                    {f.formula_code}
                    {f.display_name ? ` — ${f.display_name}` : ""}
                  </option>
                ))}
              </select>
            </Field>

            {/* Scope */}
            <Field label={L("goldenRatio.scope", "Scope", "범위")}>
              <div style={{ display: "flex" }}>
                {([
                  ["DOUGH", L("goldenRatio.dough", "Dough", "반죽")],
                  ["FILLING", L("goldenRatio.filling", "Filling", "충전물")],
                  ["BOTH", L("goldenRatio.both", "Both", "전체")],
                ] as Array<[Scope, string]>).map(([id, label], i, arr) => {
                  const active = scope === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setScope(id)}
                      style={{
                        padding: "8px 14px",
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: FONT_STACK,
                        cursor: "pointer",
                        background: active ? ACCENT : "rgba(255,255,255,0.03)",
                        color: active ? "#0a0618" : "#c4b5fd",
                        border: `1px solid ${active ? ACCENT : BORDER}`,
                        borderRadius:
                          i === 0 ? "10px 0 0 10px" : i === arr.length - 1 ? "0 10px 10px 0" : 0,
                        marginLeft: i === 0 ? 0 : -1,
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </Field>

            {/* Targets */}
            <Field label={L("goldenRatio.target", "Target", "목표") + " %"}>
              <div style={{ display: "flex", gap: 8 }}>
                {COUNTED.map((b) => (
                  <div key={b} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>{BUCKET_LABEL[b]}</span>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={target[b as "hydro" | "isolate" | "pulse"]}
                      onChange={(e) =>
                        setTarget((prev) => ({ ...prev, [b]: e.target.value }))
                      }
                      style={{
                        width: 70,
                        padding: "8px 10px",
                        borderRadius: 10,
                        background: "rgba(255,255,255,0.03)",
                        border: `1px solid ${BORDER}`,
                        color: "#f8fafc",
                        fontFamily: MONO,
                        fontSize: 14,
                      }}
                    />
                  </div>
                ))}
              </div>
            </Field>
          </div>

          {targetOff && (
            <div
              style={{
                marginTop: 16,
                display: "inline-flex",
                gap: 8,
                alignItems: "center",
                padding: "8px 12px",
                borderRadius: 10,
                background: "rgba(252, 211, 77, 0.08)",
                border: "1px solid rgba(252, 211, 77, 0.3)",
                color: "#fde68a",
                fontSize: 12,
              }}
            >
              <Warning size={16} weight="fill" color="#fcd34d" />
              {L("goldenRatio.sumWarn", "Target sum", "목표 합계")} {fmt(targetSum, 1)}
            </div>
          )}
        </Card>

        {/* Results */}
        {!picked && (
          <Empty text={L("goldenRatio.noFormula", "Pick a formula", "배합을 선택하세요")} />
        )}
        {picked && loadingRows && (
          <Empty text={L("goldenRatio.loading", "Loading", "불러오는 중")} />
        )}
        {picked && !loadingRows && entries.length === 0 && (
          <Empty text={L("goldenRatio.noRows", "No rows", "행 없음")} />
        )}

        {hasData && !loadingRows && (
          <>
            {/* Ratio table */}
            <Card>
              <SectionTitle>
                {L("goldenRatio.actual", "Actual vs target", "실제 대 목표")}
                {totals.guessCount > 0 && (
                  <span
                    style={{
                      marginLeft: 10,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "3px 8px",
                      borderRadius: 999,
                      background: "rgba(252, 211, 77, 0.1)",
                      border: "1px solid rgba(252, 211, 77, 0.35)",
                      color: "#fde68a",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: "none",
                    }}
                  >
                    <Question size={12} weight="bold" color="#fcd34d" />
                    {L("goldenRatio.provisional", "Provisional", "잠정")} ·{" "}
                    {totals.guessCount}{" "}
                    {L("goldenRatio.guessed", "guessed", "추정")}
                  </span>
                )}
              </SectionTitle>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle("left")}>{L("goldenRatio.bucketCol", "Bucket", "분류")}</th>
                    <th style={thStyle("right")}>{L("goldenRatio.target", "Target", "목표")}</th>
                    <th style={thStyle("right")}>{L("goldenRatio.byWeight", "By weight", "중량 기준")}</th>
                    <th style={thStyle("right")}>{L("goldenRatio.delta", "Delta", "차이")}</th>
                    <th style={thStyle("right")}>{L("goldenRatio.byProtein", "By protein", "단백질 기준")}</th>
                    <th style={thStyle("right")}>{L("goldenRatio.delta", "Delta", "차이")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.bucket}>
                      <td style={tdStyle("left")}>{BUCKET_LABEL[r.bucket]}</td>
                      <td style={tdStyle("right", true)}>{fmt(r.target, 1)}</td>
                      <td style={tdStyle("right", true)}>{fmt(r.byWeight, 1)}</td>
                      <td style={tdStyle("right", true)}>
                        <DeltaCell value={r.weightDelta} unit="" />
                      </td>
                      <td style={tdStyle("right", true)}>{fmt(r.byProtein, 1)}</td>
                      <td style={tdStyle("right", true)}>
                        <DeltaCell value={r.proteinDelta} unit="" />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={tfStyle("left")}>
                      {L("goldenRatio.blend", "Blend", "배합")}
                    </td>
                    <td style={tfStyle("right")} />
                    <td style={tfStyle("right")} colSpan={2}>
                      {fmt(totals.weightSum, 1)} g
                    </td>
                    <td style={tfStyle("right")} colSpan={2}>
                      {fmt(totals.proteinSum, 1)} g
                    </td>
                  </tr>
                  {totals.weight.concentrate > 0 && (
                    <tr>
                      <td style={{ ...tfStyle("left"), borderTop: "none", color: "#64748b", fontWeight: 500 }}>
                        {BUCKET_LABEL.concentrate}
                      </td>
                      <td style={{ ...tfStyle("right"), borderTop: "none" }} />
                      <td
                        style={{ ...tfStyle("right"), borderTop: "none", color: "#64748b", fontWeight: 500 }}
                        colSpan={2}
                      >
                        {fmt(totals.weight.concentrate, 1)} g
                      </td>
                      <td
                        style={{ ...tfStyle("right"), borderTop: "none", color: "#64748b", fontWeight: 500 }}
                        colSpan={2}
                      >
                        {fmt(totals.protein.concentrate, 1)} g
                      </td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </Card>

            {/* To hit target */}
            <Card>
              <SectionTitle>
                {L("goldenRatio.toHit", "To hit target", "목표 맞추기")}
                <span style={{ fontWeight: 500, color: "#64748b", fontSize: 11, marginLeft: 8 }}>
                  {L("goldenRatio.weightBasis", "weight basis", "중량 기준")}
                </span>
              </SectionTitle>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle("left")}>{L("goldenRatio.bucketCol", "Bucket", "분류")}</th>
                    <th style={thStyle("right")}>{L("goldenRatio.now", "Now", "현재")}</th>
                    <th style={thStyle("right")}>{L("goldenRatio.target", "Target", "목표")}</th>
                    <th style={thStyle("right")}>{L("goldenRatio.change", "Change", "조정")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.bucket}>
                      <td style={tdStyle("left")}>{BUCKET_LABEL[r.bucket]}</td>
                      <td style={tdStyle("right", true)}>{fmt(r.grams, 1)}</td>
                      <td style={tdStyle("right", true)}>
                        {fmt((r.target / 100) * totals.weightSum, 1)}
                      </td>
                      <td style={tdStyle("right", true)}>
                        <DeltaCell value={r.gramsDelta} unit=" g" invert />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            {/* Classification */}
            <Card>
              <SectionTitle>
                {L("goldenRatio.ingredients", "Ingredients", "원료")}
              </SectionTitle>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle("left")}>{L("goldenRatio.ingredient", "Ingredient", "원료")}</th>
                    <th style={thStyle("right")}>g</th>
                    <th style={thStyle("right")}>
                      {L("goldenRatio.proteinPer100", "Protein / 100 g", "단백질 / 100 g")}
                    </th>
                    <th style={thStyle("left")}>{L("goldenRatio.bucketCol", "Bucket", "분류")}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const b = bucketOf(e);
                    const overridden = !!overrides[e.ingredientId];
                    const guessed = isGuess(e);
                    return (
                      <tr
                        key={e.ingredientId}
                        style={guessed ? { background: "rgba(252, 211, 77, 0.05)" } : undefined}
                      >
                        <td style={tdStyle("left")}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {guessed ? (
                              <Question size={14} weight="bold" color="#fcd34d" />
                            ) : (
                              <CheckCircle size={14} weight="fill" color="#34d399" />
                            )}
                            {e.name}
                          </span>
                        </td>
                        <td style={tdStyle("right", true)}>{fmt(e.grams, 2)}</td>
                        <td style={tdStyle("right", true)}>{fmt(e.proteinPer100g, 1)}</td>
                        <td style={tdStyle("left")}>
                          <select
                            value={b}
                            onChange={(ev) =>
                              setOverrides((prev) => ({
                                ...prev,
                                [e.ingredientId]: ev.target.value as Bucket,
                              }))
                            }
                            style={{
                              ...selectStyle(160),
                              borderColor: overridden
                                ? ACCENT
                                : guessed
                                ? "rgba(252, 211, 77, 0.5)"
                                : BORDER,
                              color:
                                b === "none"
                                  ? "#64748b"
                                  : b === "concentrate"
                                  ? "#93c5fd"
                                  : "#f8fafc",
                            }}
                          >
                            {ALL_BUCKETS.map((opt) => (
                              <option key={opt} value={opt}>
                                {BUCKET_LABEL[opt]}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Small pieces ────────────────────────────────────────────────────────── */

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: CARD_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 16,
        padding: 24,
        marginBottom: 20,
        boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
        backdropFilter: "blur(20px)",
      }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        margin: "0 0 14px",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 1.2,
        textTransform: "uppercase",
        color: "#c4b5fd",
      }}
    >
      {children}
    </h2>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", letterSpacing: 0.5 }}>
        {label}
      </span>
      {children}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: "48px 24px",
        textAlign: "center",
        color: "#64748b",
        fontSize: 14,
      }}
    >
      {text}
    </div>
  );
}

/** Delta with a colour AND an arrow, so meaning never depends on colour alone.
 *  `invert` flips the arrow sense for "grams to add / remove". */
function DeltaCell({ value, unit, invert }: { value: number; unit: string; invert?: boolean }) {
  if (!isFinite(value)) return <span style={{ color: "#64748b" }}>—</span>;

  const onTarget = Math.abs(value) < 0.05;
  if (onTarget) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#34d399" }}>
        <CheckCircle size={14} weight="fill" />
        {fmt(0, 1)}
        {unit}
      </span>
    );
  }

  const over = value > 0;
  const color = over ? (invert ? "#34d399" : "#fbbf24") : invert ? "#fbbf24" : "#f0abfc";
  const Arrow = over ? ArrowUp : ArrowDown;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color }}>
      <Arrow size={13} weight="bold" />
      {signed(value, 1)}
      {unit}
    </span>
  );
}

/* ── Style helpers ───────────────────────────────────────────────────────── */

function selectStyle(width: number): React.CSSProperties {
  return {
    width,
    padding: "9px 10px",
    borderRadius: 10,
    background: "rgba(255,255,255,0.03)",
    border: `1px solid ${BORDER}`,
    color: "#f8fafc",
    fontFamily: FONT_STACK,
    fontSize: 13,
    cursor: "pointer",
  };
}

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

function thStyle(align: "left" | "right"): React.CSSProperties {
  return {
    textAlign: align,
    padding: "8px 10px",
    borderBottom: `1px solid ${BORDER}`,
    color: "#94a3b8",
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };
}

function tdStyle(align: "left" | "right", mono?: boolean): React.CSSProperties {
  return {
    textAlign: align,
    padding: "9px 10px",
    borderBottom: "1px solid rgba(139,92,246,0.08)",
    color: "#e2e8f0",
    fontFamily: mono ? MONO : FONT_STACK,
    whiteSpace: "nowrap",
  };
}

function tfStyle(align: "left" | "right"): React.CSSProperties {
  return {
    textAlign: align,
    padding: "10px",
    borderTop: `1px solid ${BORDER}`,
    color: "#c4b5fd",
    fontFamily: MONO,
    fontWeight: 700,
    whiteSpace: "nowrap",
  };
}
